import { computed } from 'vue';
import { getPathFromUri, hexToRgb } from '@/utils/helpers';
import axiosInstance from './axios.service';

const merchantId = computed(() => localStorage.getItem('merchantId') || sessionStorage.getItem('merchantId'));
const CURRENT_LOYALTY_V1_URL = '/operator/v1/loyalty/read';
const UPDATE_LOYALTY_V1_URL = '/operator/v1/loyalty/update';
const LOYALTY_DELETE_UNSUPPORTED_MESSAGE = 'Deleting loyalty is not supported by operator v1 contract';
const LOYALTY_RESPONSE_CONFIG = {
  validateStatus: (status) => ![401, 423].includes(status),
};

function unwrapCurrentLoyaltyResponse(response) {
  return response?.data && typeof response.data === 'object' ? response.data : response;
}

function getResponseMessage(response) {
  if (typeof response === 'string') {
    return response;
  }

  return response?.message || response?.data?.message || response?.error || '';
}

function isLoyaltyNotFoundResponse(response) {
  return /Loyalty program not found/i.test(getResponseMessage(response));
}

function isFailedResponse(response) {
  return response?.success === false || Boolean(response?.error);
}

function isMerchantBlockedResponse(response) {
  return response?.success === false
    && Number(response?.cause?.status) === 423
    && response?.cause?.code === 'MERCHANT_BLOCKED';
}

function assertSuccessfulLoyaltyResponse(response) {
  if (isMerchantBlockedResponse(response)) {
    const error = new Error(response.message);
    error.response = { status: 423, data: response };
    throw error;
  }

  if (isFailedResponse(response)) {
    throw new Error(getResponseMessage(response) || 'Loyalty request failed');
  }
}

function isFiniteNumberLike(value) {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  return Number.isFinite(Number(value));
}

function normalizeLevelTo(value) {
  return value === null || value === undefined ? '∞' : value;
}

function getLevelLegacyId(level, index) {
  if (level?.position !== null && level?.position !== undefined) {
    return level.position;
  }

  if (isFiniteNumberLike(level?.id)) {
    return Number(level.id);
  }

  return index + 1;
}

function normalizeLoyaltyLevel(level, index) {
  const percent = level?.bonusPercent ?? level?.discountPercent ?? level?.percent;
  const writeOffPercent = level?.maxApplyPercent ?? level?.writeOffPercent ?? percent;
  const name = level?.name ?? level?.level ?? `Уровень ${index + 1}`;

  return {
    id: getLevelLegacyId(level, index),
    backendId: level?.id,
    programId: level?.programId,
    position: level?.position,
    level: name,
    name,
    from: level?.amountFrom ?? level?.from,
    to: normalizeLevelTo(level?.amountTo ?? level?.to),
    percent,
    writeOffPercent,
    maxApplyAmount: level?.maxApplyAmount ?? level?.maxApplyBonus?.maxvalue ?? null,
  };
}

function getFirstFilledValue(items, selector) {
  const found = items.find((item) => {
    const value = selector(item);
    return value !== null && value !== undefined && value !== '';
  });

  return found ? selector(found) : null;
}

export function normalizeCurrentLoyaltyV1(response) {
  const data = unwrapCurrentLoyaltyResponse(response);

  if (!data || typeof data !== 'object') {
    return { active: [] };
  }

  const type = typeof data.type === 'string' ? data.type : '';

  if (!type) {
    return { active: [] };
  }

  const levels = Array.isArray(data.levels)
    ? data.levels.map((level, index) => normalizeLoyaltyLevel(level, index))
    : [];
  const maxApplyRate = getFirstFilledValue(levels, (level) => level.writeOffPercent);
  const maxApplyAmount = getFirstFilledValue(levels, (level) => level.maxApplyAmount);
  const active = type && data.isActive !== false ? [type] : [];

  return {
    active,
    [type]: {
      ...data,
      levels,
      maxApplyBonus: {
        rate: data?.maxApplyBonus?.rate ?? maxApplyRate,
        maxvalue: data?.maxApplyBonus?.maxvalue ?? maxApplyAmount,
      },
    },
  };
}

async function readCurrentLoyalty() {
  const response = await axiosInstance.post(CURRENT_LOYALTY_V1_URL, {}, LOYALTY_RESPONSE_CONFIG);

  if (isLoyaltyNotFoundResponse(response)) {
    return { active: [] };
  }

  assertSuccessfulLoyaltyResponse(response);

  return normalizeCurrentLoyaltyV1(response);
}

function getImagePath(uri) {
  return uri ? getPathFromUri(uri) : '';
}

function createTemplate(wallet, type) {
  const mainSettings = {
    type: 'storeCard',
    organizationName: wallet.logoText,
    logoText: wallet.logoText,
    images: {
      icon: getImagePath(wallet.logo),
      logo: getImagePath(wallet.logo),
      strip: getImagePath(wallet.image),
      iconURL: wallet.logo || '',
      logoURL: wallet.logo || '',
      stripURL: wallet.image || '',
    },
    foregroundColor: hexToRgb(wallet?.selectedPreset?.colors?.['relief-6']),
    labelColor: hexToRgb(wallet?.selectedPreset?.colors?.['relief-5']),
    backgroundColor: hexToRgb(wallet?.selectedPreset?.colors?.['relief-10']),
    secondaryFields: [
      {
        key: 'cardOwner',
        label: 'Владелец карты',
        value: '<*fullname*>',
      },
    ],
    barcode: [
      {
        message: '<*uid*>:' + merchantId.value,
        format: 'PKBarcodeFormatQR',
        messageEncoding: 'iso-8859-1',
      },
    ],
  };

  return JSON.stringify({
    ...mainSettings,
    ...loyalties[type],
  });
}

function normalizeUpdateAmountTo(value) {
  if (value === '∞' || value === Infinity || value === undefined || value === '') {
    return null;
  }

  return value;
}

function amountToCents(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalizedValue = typeof value === 'string' ? value.replace(/\s/g, '').replace(',', '.') : value;
  const numberValue = Number(normalizedValue);

  if (!Number.isFinite(numberValue)) {
    return null;
  }

  return Math.round(numberValue * 100);
}

function formatCents(cents) {
  const normalizedCents = Math.trunc(cents);
  const whole = Math.trunc(normalizedCents / 100);
  const fraction = Math.abs(normalizedCents % 100);

  return `${whole}.${String(fraction).padStart(2, '0')}`;
}

function getNextAmountFrom(previousAmountTo, fallback) {
  const previousCents = amountToCents(previousAmountTo);

  if (previousCents === null) {
    return fallback;
  }

  return formatCents(previousCents + 1);
}

function normalizeUpdateLevel(level, index, target, previousNormalizedLevel) {
  const maxApplyBonus = target?.maxApplyBonus || {};
  const maxApplyPercent = level?.writeOffPercent ?? level?.maxApplyPercent ?? maxApplyBonus.rate ?? null;
  const maxApplyAmount = maxApplyBonus.maxvalue ?? level?.maxApplyAmount ?? null;
  const amountFrom = level?.amountFrom ?? level?.from;

  return {
    name: level?.level ?? level?.name ?? `Уровень ${index + 1}`,
    amountFrom: previousNormalizedLevel ? getNextAmountFrom(previousNormalizedLevel.amountTo, amountFrom) : amountFrom,
    amountTo: normalizeUpdateAmountTo(level?.amountTo ?? level?.to),
    bonusPercent: level?.bonusPercent ?? level?.discountPercent ?? level?.percent,
    maxApplyPercent,
    maxApplyAmount,
  };
}

function createLoyaltyUpdatePayload(target, type, template) {
  const payload = {
    type,
    startBonus: target?.startBonus ?? 0,
    bonusDeferSeconds: target?.bonusDeferSeconds ?? 0,
    template,
  };

  if (Array.isArray(target?.levels)) {
    payload.levels = target.levels.reduce((levels, level, index) => {
      const previousLevel = levels[index - 1];
      levels.push(normalizeUpdateLevel(level, index, target, previousLevel));
      return levels;
    }, []);
  }

  return payload;
}

function createTemplateUpdatePayload(currentLoyalty, type, template) {
  const program = currentLoyalty?.[type] || {};

  return {
    type,
    startBonus: program.startBonus ?? 0,
    bonusDeferSeconds: program.bonusDeferSeconds ?? 0,
    template,
  };
}

const loyalties = {
  /* DISCOUNT  ====================================== */
  discount: {
    description: 'discount',
    headerFields: [
      {
        key: 'discount',
        label: 'СКИДКА',
        value: '<*percent*>%',
      },
    ],
    auxiliaryFields: [
      {
        key: 'status',
        label: 'СТАТУС',
        value: '<*level*>',
      },
      {
        key: 'toDiscount',
        label: `До скидки <*toNextPercent*>%`,
        value: '<*toNextEdge*>',
      },
    ],
  },
  /* BONUS ====================================== */
  bonus: {
    description: 'bonus',
    headerFields: [
      {
        key: 'bonus',
        label: 'БАЛАНС',
        value: '<*currentBonus*>',
      },
    ],
    auxiliaryFields: [
      {
        key: 'status',
        label: 'СТАТУС',
        value: '<*level*>',
      },
      {
        key: 'toDiscount',
        label: 'До бонуса <*toNextBonusPercent*>%',
        value: '<*toNextBonusEdge*>',
      },
    ],
  },
  /* CLUBCARD  ====================================== */
  clubcard: {
    description: 'clubcard',
    headerFields: [
      {
        key: 'deposit',
        label: 'ДЕПОЗИТ',
        value: '<*currentDeposit*>',
      },
    ],
    auxiliaryFields: [
      {
        key: 'logoText',
        value: '<*logoText*>',
      },
      {
        key: 'cardNumber',
        label: 'НОМЕР КАРТЫ',
        value: '<*externalId*>',
      },
    ],
  },
  /* COUPON {START, END}  ====================================== */
  coupon_start: {
    description: 'coupon_start',
    auxiliaryFields: [
      {
        key: 'toGift',
        label: '<*signatureForAccumulation*>',
      },
      {
        key: 'points',
        value: '<*currentPoint*>/<*activateОnValue*>',
      },
    ],
  },
  coupon_end: {
    description: 'coupon_end',
    auxiliaryFields: [
      {
        key: 'getGift',
        label: '<placeToFillCongratulations*>',
      },
    ],
  },
};

export const getLoyalties = async () => {
  try {
    return await readCurrentLoyalty();
  } catch (error) {
    if ([401, 423].includes(error.response?.status)) {
      throw error;
    }

    console.log('getLoyalties:fnFirebase.js ===>>> ', error);
  }
};
export const fnGetLoyalties = async () => {
  try {
    const result = await readCurrentLoyalty();
    const type = result?.active?.[0];

    return type ? result?.[type]?.levels || [] : [];
  } catch (error) {
    console.log('getLoyalties:fnFirebase.js ===>>> ', error);
  }
};

export const fnGetLoyaltiesType = async () => {
  try {
    const result = await readCurrentLoyalty();
    const type = result?.active?.[0];

    return type ? result?.[type] || null : null;
  } catch (error) {
    console.log('getLoyalties:fnFirebase.js ===>>> ', error);
  }
};
export const getStatusLoyalties = async () => {
  try {
    const result = await readCurrentLoyalty();
    return result.active;
  } catch (error) {
    if (error === 'Unauthorized') {
      return null;
    }
    console.log('getStatusLoyalties', error);
  }
};
export const getLoyaltyAndType = async () => {
  try {
    const result = await readCurrentLoyalty();
    const type = result?.active?.[0] || null;

    return {
      active: result?.active || [],
      type,
      loyalty: type ? result?.[type] || null : null,
    };
  } catch (error) {
    console.log('getStatusLoyalties', error);
  }
};

export const getDataLoyalties = async (type) => {
  try {
    const result = await readCurrentLoyalty();

    // for (const key in result) {
    //   if (
    //     key === "maxApplyBonus" &&
    //     typeof result[key]?.rate === "number" &&
    //     result[key]?.rate < 1
    //   ) {
    //     result[key].rate = Math.round(+result[key]?.rate * 100);
    //   }
    // }
    return result?.[type] || {};
  } catch (error) {
    console.log('getDataLoyalties', error);
  }
};
export const deleteLoyalty = async () => {
  await readCurrentLoyalty();
  throw new Error(LOYALTY_DELETE_UNSUPPORTED_MESSAGE);
};

/* ============================================================== */

export const saveDataLoyalties = async (target, type) => {
  try {
    const urlWallet = `/operator/wallet/read`;
    const wallet = await axiosInstance.get(urlWallet);
    const template = createTemplate(wallet, type);
    const newLoyalty = createLoyaltyUpdatePayload(target, type, template);

    try {
      const response = await axiosInstance.post(UPDATE_LOYALTY_V1_URL, newLoyalty, LOYALTY_RESPONSE_CONFIG);
      assertSuccessfulLoyaltyResponse(response);

      return response;
    } catch (error) {
      console.error(error.message);
      throw error;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const updateTemplate = async (wallet) => {
  // const { merchantName } = merchant.value;
  try {
    const currentLoyalty = await readCurrentLoyalty();
    const type = currentLoyalty?.active?.[0];

    if (!type) {
      return null;
    }

    const template = createTemplate(wallet, type);
    const updatePayload = createTemplateUpdatePayload(currentLoyalty, type, template);

    try {
      const response = await axiosInstance.post(UPDATE_LOYALTY_V1_URL, updatePayload, LOYALTY_RESPONSE_CONFIG);
      assertSuccessfulLoyaltyResponse(response);
      return response;
    } catch (error) {
      console.error(error.message);
      throw error;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/* ------------------------------------------- */
export const getUserCards = async (phone) => {
  const url = `/operator/order/card-by-phone-btoa?phone=${btoa(phone)}`;
  // /operator/order/card-by-phone-btoa

  try {
    const cards = await axiosInstance.get(url);
    return cards;
  } catch (error) {
    console.log(error);
    console.log('getUserCards:error ===>>> ', error);
  }
};
export const getStatusMessengerIntegrations = async () => {
  const url = `/operator/merchant/integrations`;
  try {
    const response = await axiosInstance.get(url);
    const keys = Object.keys(response);

    return keys.includes('telegram');
  } catch (error) {
    console.log('getStatusLoyalties', error);
  }
};

export const updateDeposit = async ({ userId = 'A012', amount = 0 }) => {};
